import { useEffect, useState } from "react";

/**
 * Loads a form's initial values once on mount. Returns `[values, error]`, both
 * `null` until the request settles. Only the first render's `load` and
 * `fallbackError` are used, so callers may pass inline closures.
 */
const useAsyncInitialValues = <T>(load: () => Promise<T>, fallbackError: string) => {
  const [values, setValues] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    load()
      .then(nextValues => {
        if (!cancelled) {
          setValues(nextValues);
        }
      })
      .catch((loadError: unknown) => {
        if (!cancelled) {
          setError(loadError instanceof Error ? loadError.message : fallbackError);
        }
      });

    return () => {
      cancelled = true;
    };
    // Intentionally runs once: the values seed a form and must not reload.
  }, []);

  return [values, error] as const;
};

export default useAsyncInitialValues;
